import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  ButtonGroup,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { getMeeshoOrderReturnList } from "app/redux/slice/meeshoOrderManagementSlice";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount || 0);

const EXCLUDED_KEYS = ["_id", "__v", "createdAt", "updatedAt", "deletedAt", "order"];

const formatFieldLabel = (key) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return moment.utc(value).format("DD/MM/YYYY");
  }
  return String(value);
};

const MeeshoReturnsList = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const returns = meeshoData?.return?.list || [];

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [returnDetails, setReturnDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("paymentDate");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-returns-list" });

  useEffect(() => {
    fetchReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, dateRange, order, orderBy]);

  const fetchReturns = () => {
    const params = { page, limit: rowsPerPage, search: searchTerm.trim() };
    if (dateRange[0] && dateRange[1]) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getMeeshoOrderReturnList(params));
  };

  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const columns = [
    { id: "subOrderNo", label: "Sub Order No", sort: false },
    {
      id: "productName",
      label: "Product",
      sort: false,
      render: (row) => (
        <Typography noWrap variant="body2" sx={{ maxWidth: 200 }}>
          {row?.productName || row?.order?.productName || "-"}
        </Typography>
      ),
    },
    { id: "supplierSku", label: "SKU", sort: false },
    {
      id: "order.customerState",
      label: "State",
      sort: false,
      render: (row) => row?.order?.customerState || "-",
    },
    {
      id: "orderDate",
      label: "Order Date",
      sort: false,
      render: (row) =>
        row?.orderDate ? moment.utc(row.orderDate).format("DD/MM/YYYY") : "-",
    },
    {
      id: "paymentDate",
      label: "Return Payment Date",
      sort: false,
      render: (row) =>
        row?.paymentDate
          ? moment.utc(row.paymentDate).format("DD/MM/YYYY")
          : "-",
    },
    {
      id: "listingPrice",
      label: "Listing Price",
      sort: false,
      isTotal: true,
      format: (v) => formatCurrency(v),
    },
    {
      id: "finalSettlementAmount",
      label: "Settlement Amount",
      sort: false,
      isTotal: true,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            color:
              (row?.finalSettlementAmount || 0) < 0
                ? "error.main"
                : "text.primary",
            fontWeight: 600,
          }}
        >
          {formatCurrency(row?.finalSettlementAmount)}
        </Typography>
      ),
    },
    {
      id: "paymentStatus",
      label: "Payment Status",
      sort: false,
      render: (row) =>
        row?.paymentStatus ? (
          <Chip
            label={row.paymentStatus}
            color={
              row.paymentStatus === "APPROVED"
                ? "success"
                : row.paymentStatus === "REJECTED"
                ? "error"
                : "warning"
            }
            size="small"
          />
        ) : (
          "-"
        ),
    },
    {
      id: "order.status",
      label: "Order Status",
      sort: false,
      render: (row) =>
        row?.order?.status ? (
          <Chip label={row.order.status} size="small" variant="outlined" />
        ) : (
          "-"
        ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedReturn(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Returns
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by sub order no, product, SKU..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <ButtonGroup size="small" variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button key={r.key} onClick={() => setTempDateRange(r.getRange())}>
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                variant="contained"
                onClick={applyFilters}
                fullWidth
                size="small"
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                variant="outlined"
                onClick={clearAll}
                startIcon={<RefreshIcon />}
                fullWidth
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {meeshoData?.return?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Total Returns:
              </Typography>
              <Chip
                label={meeshoData?.return?.total ?? 0}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Stack>
            <CustomTable
              columns={columns}
              rows={returns}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.return?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={(o, ob) => { setOrder(o); setOrderBy(ob); }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.return?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedReturn) {
              setReturnDetails(selectedReturn);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Return Details</DialogTitle>
        <DialogContent dividers>
          {returnDetails && (
            <Grid container spacing={2}>
              {Object.entries(returnDetails)
                .filter(([key]) => !EXCLUDED_KEYS.includes(key))
                .map(([key, value], i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2">
                        {formatFieldLabel(key)}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatFieldValue(key, value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              {returnDetails?.order && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
                      Order Details
                    </Typography>
                  </Grid>
                  {Object.entries(returnDetails.order)
                    .filter(([key]) => !["_id", "__v"].includes(key))
                    .map(([key, value], i) => (
                      <Grid item xs={12} md={6} key={`order-${i}`}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2">
                            {formatFieldLabel(key)}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatFieldValue(key, value)}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeeshoReturnsList;
